import React, { useState, useEffect } from 'react';
import ContactsHeader from './components/ContactsHeader/ContactsHeader';
import ContactForm from './components/ContactForm/ContactForm';
import StageTabs from './components/StageTabs/StageTabs';
import ContactTable from './components/ContactTable/ContactTable';
import ContactDetailPopup from './components/ContactDetailPopup/ContactDetailPopup';
import TasksPopup from './components/TasksPopup/TasksPopup';
import { contactsApi } from './services/contactsApi';
import './Contacts.css';

function Contacts({ user, onLogout, onNavigate }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [viewingTasks, setViewingTasks] = useState(null);
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: '',
    country: '',
    source: ''
  });
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterTaskStatus, setFilterTaskStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');

  const stages = [
    { key: 'all', name: 'All Contacts', color: '#6b7280', icon: '📋' },
    { key: 'lead', name: 'Leads', color: '#3b82f6', icon: '👥' },
    { key: 'customer', name: 'Customers', color: '#10b981', icon: '🛒' },
    { key: 'marketer', name: 'Marketers', color: '#f59e0b', icon: '🚀' },
    { key: 'leader', name: 'Leaders', color: '#8b5cf6', icon: '👑' }
  ];

  // Fetch contacts using API service
  const fetchContacts = async () => {
    try {
      const data = await contactsApi.fetchAll();
      if (data.success) {
        setContacts(data.contacts);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Enhanced filtering with search and filters
  const filteredContacts = contacts.filter(c => {
    // Stage filter
    if (activeStage !== 'all' && c.status !== activeStage) return false;
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = 
        c.first_name.toLowerCase().includes(q) ||
        c.last_name.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q)) ||
        (c.city && c.city.toLowerCase().includes(q)) ||
        (c.country && c.country.toLowerCase().includes(q));
      if (!matches) return false;
    }
    
    // Source filter
    if (filterSource !== 'all' && c.source !== filterSource) return false;
    
    // Task progress filter
    if (filterTaskStatus === 'completed' && c.done_tasks !== c.total_tasks) return false;
    if (filterTaskStatus === 'in_progress' && (c.done_tasks === 0 || c.done_tasks === c.total_tasks)) return false;
    if (filterTaskStatus === 'not_started' && c.done_tasks !== 0) return false;
    
    // Date range filter
    if (filterDateRange !== 'all') {
      const contactDate = new Date(c.created_at);
      const daysAgo = Math.floor((Date.now() - contactDate) / (1000 * 60 * 60 * 24));
      if (filterDateRange === '7d' && daysAgo > 7) return false;
      if (filterDateRange === '30d' && daysAgo > 30) return false;
      if (filterDateRange === '90d' && daysAgo > 90) return false;
    }
    
    return true;
  });
  
  // Calculate active filter count (excluding stage filter from main tabs)
  const activeFilterCount = [
    searchQuery !== '',
    filterSource !== 'all',
    filterTaskStatus !== 'all',
    filterDateRange !== 'all'
  ].filter(Boolean).length;
  
  // Clear all filters function
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterSource('all');
    setFilterTaskStatus('all');
    setFilterDateRange('all');
  };

  // Handlers using API service
  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const data = await contactsApi.add(newContact);
      if (data.success) {
        setContacts([data.contact, ...contacts]);
        setNewContact({ first_name: '', last_name: '', email: '', phone: '', city: '', country: '', source: '' });
        setShowAddForm(false);
      } else {
        alert('Error adding contact: ' + data.error);
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Error adding contact: ' + error.message);
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    try {
      const data = await contactsApi.update(editingContact);
      if (data.success) {
        setContacts(contacts.map(c => c.id === data.contact.id ? data.contact : c));
        setEditingContact(null);
      } else {
        alert('Error updating: ' + data.error);
      }
    } catch (error) {
      console.error('Update failed:', error);
      alert('Error updating contact');
    }
  };

  const handleDeleteFromView = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      const data = await contactsApi.delete(contactId);
      if (data.success) {
        setContacts(contacts.filter(c => c.id !== contactId));
        setViewingContact(null);
      } else {
        alert('Error deleting contact: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error deleting contact');
    }
  };

  const handleStatusChange = (contactId, newStatus) => {
    setContacts(prevContacts => 
      prevContacts.map(contact => 
        contact.id === contactId ? { ...contact, status: newStatus } : contact
      )
    );
  };

  const handleTaskUpdate = (contactId, doneCount, totalCount) => {
    setContacts(contacts.map(c => 
      c.id === contactId ? { ...c, done_tasks: doneCount, total_tasks: totalCount } : c
    ));
  };


  if (loading) {
    return (
      <div className="contacts-container">
        <div className="loading">Loading contacts...</div>
      </div>
    );
  }

  return (
    <div className="contacts-container">
      <ContactsHeader
        contactCount={contacts.length}
        showAddForm={showAddForm}
        onNavigate={onNavigate}
        onToggleForm={() => setShowAddForm(!showAddForm)}
      />

      {showAddForm && !editingContact && (
        <ContactForm
          mode="add"
          contact={newContact}
          onChange={setNewContact}
          onSubmit={handleAddContact}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingContact && (
        <ContactForm
          mode="edit"
          contact={editingContact}
          onChange={setEditingContact}
          onSubmit={handleUpdateContact}
          onCancel={() => setEditingContact(null)}
        />
      )}

      <StageTabs
        stages={stages}
        activeStage={activeStage}
        contacts={contacts}
        onStageChange={setActiveStage}
        searchQuery={searchQuery}
        filterSource={filterSource}
        filterTaskStatus={filterTaskStatus}
        filterDateRange={filterDateRange}
        onSearchChange={setSearchQuery}
        onSourceChange={setFilterSource}
        onTaskStatusChange={setFilterTaskStatus}
        onDateRangeChange={setFilterDateRange}
        onClearFilters={clearAllFilters}
        activeFilterCount={activeFilterCount}
      />

      <ContactTable
        contacts={filteredContacts}
        onViewContact={setViewingContact}
        onViewTasks={setViewingTasks}
      />

      {viewingContact && (
        <ContactDetailPopup
          contact={viewingContact}
          onClose={() => setViewingContact(null)}
          onEdit={(contact) => {
            setEditingContact(contact);
            setViewingContact(null);
          }}
          onDelete={handleDeleteFromView}
        />
      )}

      {viewingTasks && (
        <TasksPopup
          contact={viewingTasks}
          onClose={() => setViewingTasks(null)}
          onTaskUpdate={handleTaskUpdate}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

export default Contacts;
